<template>
  <div v-if="schedules.length <= 0 && budgets.length <= 0" class="appt-empty">
    <h2>Nenhum agendamento por enquanto</h2>
    <p>Encontre um profissional e marque o horário que funcionar para você.</p>
    <router-link to="/" class="btn btn-primary">Nova consulta</router-link>
  </div>

  <div v-else>
    <p class="appt-list-title" v-if="schedules.length">Próximos agendamentos</p>

    <article
      class="appt-card"
      :class="{ 'is-next': index === 0 && schedule.sched_status !== 'CA', 'is-cancelled': schedule.sched_status === 'CA' }"
      v-for="(schedule, index) in schedules"
      :key="schedule.id_sched"
    >
      <div class="appt-card__head">
        <img
          v-if="schedule.prof_photo"
          :src="url(schedule.prof_photo)"
          class="appt-card__photo"
          alt=""
        >
        <img
          v-else
          src="../../assets/images/no-photo.png"
          class="appt-card__photo"
          alt=""
        >
        <div>
          <p class="appt-card__name">{{ schedule.prof_name }}</p>
          <div class="appt-card__chips">
            <span class="appt-chip" v-if="schedule.spec_name">{{ schedule.spec_name }}</span>
            <span class="appt-chip is-ghost">{{ schedule.avail_online ? 'Online' : 'Presencial' }}</span>
          </div>
        </div>
        <span class="appt-status" :class="statusClass(schedule)">{{ schedule.status }}</span>
      </div>

      <p class="appt-card__when">{{ whenLabel(schedule.sched_datetime_patient) }}</p>
      <p class="appt-card__meta">
        {{ placeLine(schedule) }}
        <template v-if="schedule.patient_name"> · {{ schedule.patient_name }}</template>
        <template v-if="schedule.sched_value > 0"> · R$ {{ money(schedule.sched_value) }}</template>
        <template v-if="schedule.paymeth_name"> · {{ schedule.paymeth_name }}</template>
      </p>

      <div class="appt-card__actions" v-if="schedule.sched_status !== 'CA'">
        <button
          type="button"
          class="btn btn-primary"
          v-if="schedule.sched_status === 'AP' && schedule.paymeth_type === 'PI'"
          @click="showPixData(schedule)"
        >
          Pagar agora
        </button>
        <button
          type="button"
          class="btn btn-primary"
          v-if="schedule.allow_entry_queue"
          @click="startAttendanceOnline(schedule)"
        >
          Entrar na consulta
        </button>
        <button
          type="button"
          class="btn btn-ghost"
          v-if="schedule.allow_reschedule"
          @click="onClickToReSchedule(schedule)"
        >
          Remarcar
        </button>
        <button
          type="button"
          class="btn btn-ghost"
          v-if="schedule.allow_cancel"
          @click="cancelSchedule(schedule)"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-ghost"
          v-if="schedule.sched_status === 'AP' && schedule.paymeth_type === 'PI'"
          @click="rejectPayment(schedule)"
        >
          Desistir
        </button>
      </div>
    </article>

    <p class="appt-list-title" v-if="budgets.length" :style="schedules.length ? 'margin-top: 28px' : ''">Orçamentos</p>

    <article
      class="appt-card"
      :class="{ 'is-cancelled': budget.budget_status === 'cancelled' }"
      v-for="budget in budgets"
      :key="budget.id_budget"
    >
      <div class="appt-card__head">
        <img
          v-if="budget.place.place_logo"
          :src="budget.place.place_logo"
          class="appt-card__photo"
          alt=""
        >
        <img
          v-else
          src="../../assets/images/no-photo.png"
          class="appt-card__photo"
          alt=""
        >
        <div>
          <p class="appt-card__name">{{ budget.place.place_name }}</p>
          <div class="appt-card__chips">
            <span class="appt-chip is-ghost">{{ budget.budget_type_dsc }}</span>
          </div>
        </div>
        <span class="appt-status" :class="budgetStatusClass(budget)">{{ budget.budget_status_dsc }}</span>
      </div>

      <p class="appt-card__when" v-if="budgetValue(budget)">R$ {{ money(budgetValue(budget)) }}</p>
      <p class="appt-card__meta">
        {{ budget.place.place_full_address }}
        <template v-if="budget.patient?.patient_name"> · {{ budget.patient.patient_name }}</template>
        <template v-if="budget.payment_method?.paymeth_name"> · {{ budget.payment_method.paymeth_name }}</template>
        <template v-if="budget.insurance"> · {{ budget.insurance.insurance_name }}</template>
        <template v-if="budget.budget_valid_until">
          · válido até {{ date(budget.budget_valid_until).format('DD/MM/YYYY') }}
        </template>
      </p>
      <p class="appt-card__meta" v-if="Number(budget.budget_discount || 0) > 0">
        Já com desconto de R$ {{ money(budget.budget_discount) }}
      </p>

      <div class="appt-card__actions" v-if="budget.budget_status !== 'cancelled'">
        <button type="button" class="btn btn-ghost" @click="showItems(budget)">Ver itens</button>
        <button
          type="button"
          class="btn btn-primary"
          v-if="budget.budget_status === 'waiting_payment' && !budget.budget_expired"
          @click="showPixDataBudget(budget)"
        >
          Pagar agora
        </button>
        <button
          type="button"
          class="btn btn-ghost"
          v-if="budget.budget_status === 'waiting_payment'"
          @click="cancel(budget)"
        >
          Cancelar
        </button>
      </div>
      <p class="appt-card__meta" v-if="budget.budget_expired && budget.budget_status === 'waiting_payment'">
        Orçamento vencido. Fale com a clínica para gerar um novo.
      </p>
    </article>
  </div>

  <div class="modal fade" ref="modal_availability_prof" id="modal_availability_prof" tabindex="1">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Escolha um dia e horário</h5>
          <div class="btn btn-icon btn-sm btn-active-light-primary ms-2"
               data-bs-dismiss="modal" aria-label="Fechar">
            <i class="bi bi-x-lg"></i>
          </div>
        </div>
        <div class="modal-body">
          <Times v-if="currentSchedule != null"
                 :id_prof="currentSchedule.id_prof"
                 :id_place="currentSchedule.id_place"
                 :id_spec="currentSchedule.id_spec"
                 :id_city="currentSchedule.sched_online ? null : currentSchedule.id_city"
                 :id_insurance="currentSchedule.id_insurance"
                 :id_paymeth="currentSchedule.id_paymeth"
                 :id_sched_father="currentSchedule.id_sched"
                 :professional="professional"
                 @onClickDate="selectAvailability"
          />
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">Fechar</button>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import http from "@/helpers/http";
import date from "@/helpers/date";
import Alert from "@/helpers/alert";
import Swal from "sweetalert2";
import Times from "../components/Times";
import str from "../../helpers/str";
import Device from "../../helpers/device";

export default {
  name: "Schedules",
  components: {
    Times,
  },
  data() {
    return {
      schedules: [],
      budgets: [],
      professional: {},
      currentSchedule: null,
      startEndDays: this.isMobile() ? 3 : 7,
    }
  },
  computed: {
    userAuth() {
      return this.$store.state.auth.user;
    }
  },
  mounted() {
    this.loadSchedules();
    this.loadBudgets();
  },
  methods: {
    date: date,
    url(path) {
      return str.url(path)
    },
    isMobile() {
      return Device.isMobile();
    },
    cancel(budget) {
      Swal.fire({
        title: 'Cancelar',
        text: "Tem certeza que deseja cancelar?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#780f5a',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sim, Cancelar!',
        cancelButtonText: "Não"
      }).then((result) => {
        if (result.isConfirmed) {
          http.patch(`/users/${this.userAuth.id_user}/budgets/${budget.id_budget}/cancel`).then(() => {
            this.loadBudgets();
            Alert.success('Cancelamento realizado com sucesso.');
          }).catch((e) => Alert.error(e.response.data));
        }
      })
    },
    money(value) {
      return Number(value ?? 0).toLocaleString('pt-br', {
        style: 'decimal',
        minimumFractionDigits: 2,
      });
    },
    whenLabel(datetime) {
      if (!datetime) {
        return '';
      }
      return date(datetime).format('dddd, DD [de] MMMM [·] HH:mm');
    },
    placeLine(schedule) {
      return [schedule.place_name, schedule.place_address].filter(Boolean).join(' · ');
    },
    statusClass(schedule) {
      const status = schedule.sched_status;
      if (['CA', 'CM', 'PR', 'FA'].includes(status)) {
        return 'is-bad';
      }
      if (['AP', 'AL', 'RE'].includes(status)) {
        return 'is-warn';
      }
      return 'is-ok';
    },
    budgetStatusClass(budget) {
      const status = budget.budget_status;
      if (['cancelled', 'payment_failed'].includes(status) || budget.budget_expired) {
        return 'is-bad';
      }
      if (['pending', 'waiting_payment'].includes(status)) {
        return 'is-warn';
      }
      return 'is-ok';
    },
    budgetValue(budget) {
      return Number(budget.budget_value ?? 0);
    },
    /** Item do catálogo pode não ser exame: aí vale o nome cadastrado. */
    itemLabel(item) {
      const exam = item.item?.exam;
      if (exam) return [exam.exam_tuss_code, exam.exam_name].filter(Boolean).join(' - ');
      return item.item?.item_name ?? 'Item';
    },
    showItems(budget) {
      const hasQuantity = (budget.budget_items ?? []).some(item => Number(item.item_quantity ?? 1) > 1);
      let itemsHtml = '<table class="table table-striped"><thead><tr><th class="text-start">Item</th>'
          + (hasQuantity ? '<th class="text-center">Qtde.</th>' : '')
          + '<th style="min-width: 25%;">Valor</th></tr></thead><tbody>';

      (budget.budget_items ?? []).forEach(item => {
        const quantity = Math.max(1, Number(item.item_quantity ?? 1));
        const unit = Number(item.item_value_paid ?? item.item_value ?? 0);
        itemsHtml += `<tr>
          <td class="text-start">${this.itemLabel(item)} <br>${item.item?.item_description ?? ''}</td>
          ${hasQuantity ? `<td class="text-center">${quantity}</td>` : ''}
          <td>R$ ${this.money(unit * quantity)}${quantity > 1 ? `<br><small>${quantity} x R$ ${this.money(unit)}</small>` : ''}</td>
        </tr>`;
      });
      itemsHtml += '</tbody>';

      if (Number(budget.budget_discount || 0) > 0) {
        const columns = hasQuantity ? 2 : 1;
        itemsHtml += `<tfoot>
          <tr><td class="text-start" colspan="${columns}">Subtotal</td><td>R$ ${this.money(budget.budget_subtotal ?? budget.budget_value)}</td></tr>
          <tr class="text-success"><td class="text-start" colspan="${columns}">Desconto</td><td>- R$ ${this.money(budget.budget_discount)}</td></tr>
          <tr class="fw-bold"><td class="text-start" colspan="${columns}">Total</td><td>R$ ${this.money(budget.budget_value)}</td></tr>
        </tfoot>`;
      }

      itemsHtml += '</table>';
      Swal.fire({
        title: 'Lista de itens',
        html: itemsHtml,
        showCloseButton: true,
        showCancelButton: false,
        showConfirmButton: false,
        width: this.isMobile() ? '95%' : '60%',
      });
    },
    loadSchedules() {
      return http.get(`/users/${this.userAuth.id_user}/schedules`).then((response) => {
        this.schedules = response.data;
      });
    },
    loadBudgets() {
      return http.get(`/users/${this.userAuth.id_user}/budgets`).then((response) => {
        this.budgets = response.data;
      });
    },
    startAttendanceOnline(schedule) {
      Swal.fire({
        title: 'Atenção',
        text: "Tem certeza que deseja iniciar o atendimento online?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#780f5a',
        cancelButtonColor: '#ccc',
        confirmButtonText: 'Sim, Entrar na fila!',
        cancelButtonText: "Não"
      }).then((result) => {
        if (result.isConfirmed) {
          if (schedule.started_at != null && schedule.finished_at == null) {
            this.$router.push(`/schedules/${this.scheduleId}/attendance`);
            return;
          }

          http.post(`/users/${this.userAuth.id_user}/schedules/${schedule.id_sched}/queues/entry`).then((response) => {
            this.$router.push(`/schedules/${schedule.id_sched}/queues/${response.data.id_queue}`);
            return response;
          }).catch((e) => {
            console.log(e);
            let response = e.response?.data;
            Alert.error(response);
          });
        }
      });
    },
    cancelSchedule(schedule) {
      Swal.fire({
        title: 'Cancelar',
        text: "Tem certeza que deseja cancelar essa consulta?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#780f5a',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sim, Cancelar!',
        cancelButtonText: "Não"
      }).then((result) => {
        if (result.isConfirmed) {
          http.patch(`/users/${this.userAuth.id_user}/schedules/${schedule.id_sched}/cancel`).then(() => {
            this.loadSchedules();
            Alert.success('Cancelamento realizado com sucesso.');
          }).catch((e) => Alert.error(e.response.data));
        }
      })
    },
    onClickToReSchedule(schedule) {
      this.professional = {};

      let accessToken = this.$store.state.auth.token;
      let combinedString = `${accessToken}@${this.userAuth.id_user}@${schedule.id_sched}`;
      let base64String = btoa(combinedString);

      this.$router.push({path: '/schedules/reschedule', query: {params: base64String}})
    },
    rejectPayment(schedule) {
      Swal.fire({
        title: 'Cancelar pagamento',
        text: "Tem certeza que deseja cancelar?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, cancelar!',
        cancelButtonText: "Não, Realizar pagamento"
      }).then((result) => {
        if (result.isConfirmed) {
          http.patch(`/users/${this.userAuth.id_user}/schedules/${schedule.id_sched}/reject-payment`).then(() => {
            this.loadSchedules();
          });
        }
      });
    },
    showPixDataBudget(budget) {
      http.get(`/users/${this.userAuth.id_user}/budgets/${budget.id_budget}/payment-data`).then((response) => {
        this.$store.commit('toSchedule/setPixData', response.data);
        this.$router.push('/pix');
      }).catch(e => {
        Alert.error(e.response?.data ?? 'Não foi possível abrir o pagamento.');
      });
    },
    showPixData(schedule) {
      http.get(`/users/${this.userAuth.id_user}/schedules/${schedule.id_sched}/payment-data`).then((response) => {
        this.$store.commit('toSchedule/setPixData', response.data);
        this.$router.push('/pix');
      });
    },
    reSchedule(availabilityTime) {
      let param = {
        sched_date: availabilityTime.available_date,
        sched_time: availabilityTime.available_time,
        sched_datetime_patient: availabilityTime.sched_datetime_patient,
      };
      if (availabilityTime.id_avail != null && availabilityTime.id_avail !== '') {
        param.id_avail = availabilityTime.id_avail;
      }
      if (availabilityTime.id_place_location) {
        param.id_place_location = availabilityTime.id_place_location;
      }

      http.post(`/users/${this.userAuth.id_user}/schedules/${this.currentSchedule.id_sched}/reschedule`, param).then(async () => {
        await this.loadSchedules();

        let modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('modal_availability_prof'));
        modal.toggle();

        Alert.success('Reagendamento realizado com sucesso.');
      }).catch((e) => Alert.error(e.response.data));
    },
    selectAvailability(availabilityTime) {
      this.reSchedule(availabilityTime);
    },
  }
}
</script>

<style scoped>

</style>
